import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Key, Copy, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Two-step dialog:
 *   1) confirm regeneration
 *   2) display the generated password once with copy-to-clipboard
 */
export default function PasswordResetDialog({ open, onOpenChange, user }) {
  const [step, setStep] = useState("confirm"); // 'confirm' | 'result'
  const [busy, setBusy] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [copied, setCopied] = useState(false);

  const reset = () => { setStep("confirm"); setNewPw(""); setCopied(false); };
  const close = (o) => { onOpenChange(o); if (!o) reset(); };

  const generate = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const r = await api.post(`/users/${user.id}/reset-password`);
      setNewPw(r.data.password);
      setStep("result");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(newPw);
      setCopied(true);
      toast.success("Mot de passe copié");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copie impossible — sélectionne manuellement");
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl"
        data-testid="pw-reset-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Key size={18} weight="duotone" className="text-amber-400" />
            {step === "confirm" ? "Générer un nouveau mot de passe" : "Nouveau mot de passe"}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs">
            {step === "confirm"
              ? `Le mot de passe actuel de ${user?.name || "cet équipier"} sera immédiatement invalidé.`
              : "Copie-le maintenant — il ne sera plus jamais affiché."}
          </DialogDescription>
        </DialogHeader>

        {step === "confirm" && (
          <div className="space-y-3">
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 flex gap-2 text-xs text-amber-200">
              <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <div>
                Un mot de passe fort et aléatoire va être généré. Copie-le et transmets-le à <b>{user?.name}</b> par un canal sûr (SMS, en main propre).
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1 border border-white/10" onClick={() => close(false)} data-testid="pw-reset-cancel">
                Annuler
              </Button>
              <Button
                onClick={generate}
                disabled={busy}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-medium"
                data-testid="pw-reset-confirm"
              >
                {busy ? "Génération…" : "Générer"}
              </Button>
            </div>
          </div>
        )}

        {step === "result" && (
          <div className="space-y-3">
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200 flex gap-2">
              <CheckCircle size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <div>Mot de passe régénéré. Cette fenêtre est ta <b>seule occasion</b> de le voir.</div>
            </div>

            <div className="rounded-xl bg-white/[0.06] border border-white/15 p-4 flex items-center gap-2">
              <code
                className="flex-1 font-mono text-xl tracking-wider select-all break-all"
                data-testid="pw-reset-value"
              >
                {newPw}
              </code>
              <button
                onClick={copy}
                className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition ${
                  copied ? "bg-emerald-500/25 text-emerald-300" : "bg-[#005CFF]/25 text-[#7CB2FF] hover:bg-[#005CFF]/40"
                }`}
                title="Copier"
                data-testid="pw-reset-copy"
              >
                {copied ? <CheckCircle size={18} weight="fill" /> : <Copy size={16} />}
              </button>
            </div>

            <div className="text-[10px] text-white/40">
              Enregistré dans le Journal d&apos;audit sous <code className="text-white/60">user.password_reset</code>.
            </div>

            <Button
              onClick={() => close(false)}
              className="w-full bg-[#005CFF] hover:bg-[#0047CC]"
              data-testid="pw-reset-done"
            >
              Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
